var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { normaliseConfigKey, } from '../../../components/FormComponents/dynamicForm/field_config_schema';
import { FormFieldType, } from '../../../interfaces/createApplication';
import { API_FIELD } from './api_field_names';
import { CREATE_APPLICATION_STEP_ID, CUSTOMER_LOOKUP_KEY, } from './create_application_schema';
import { amountValidation, COUNTRY_OPTIONS, formatAmountInput, formatPercentageInput, formatPhoneInput, getAllTitleOptions, getStateOptions, percentageValidation, quantityValidation, yearValidation, } from './form_field_factories';
export var IGNORED_CONFIG_GROUP_KEYS = ['pricing'];
export var isIgnoredConfigGroup = function (groupKey) {
    return IGNORED_CONFIG_GROUP_KEYS.includes(groupKey);
};
export var STEP_ID_BY_CONFIG_GROUP = {
    customerinformation: CREATE_APPLICATION_STEP_ID.CustomerInformation,
    guarantorinformation: CREATE_APPLICATION_STEP_ID.GuarantorInformation,
    documentsignerinformation: CREATE_APPLICATION_STEP_ID.DocumentSignerInformation,
    assetinformation: CREATE_APPLICATION_STEP_ID.AssetInformation,
    filesupload: CREATE_APPLICATION_STEP_ID.FilesFinanceAndComments,
    lineofcreditrequest: CREATE_APPLICATION_STEP_ID.FilesFinanceAndComments,
    financeoptions: CREATE_APPLICATION_STEP_ID.FilesFinanceAndComments,
};
var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
var EMAIL_SEPARATOR_PATTERN = /[;,]\s*/;
var asKeySet = function (apiFieldNames) {
    return new Set(apiFieldNames.map(normaliseConfigKey));
};
var COUNTRY_FIELDS = asKeySet([
    API_FIELD.Country,
    API_FIELD.CustomerCountry,
    API_FIELD.BillingCountry,
]);
var STATE_FIELDS = asKeySet([API_FIELD.State, API_FIELD.CustomerState, API_FIELD.BillingState]);
var TAXPAYER_ID_FIELDS = asKeySet([API_FIELD.TaxpayerId]);
var ADDRESS_FIELDS = asKeySet([API_FIELD.Address, API_FIELD.BillingAddress]);
var CITY_FIELDS = asKeySet([API_FIELD.City, API_FIELD.BillingCity]);
var POSTAL_CODE_FIELDS = asKeySet([API_FIELD.PostalCode, API_FIELD.BillingPostalCode]);
var AMOUNT_FIELDS = asKeySet([
    API_FIELD.DownPayment,
    API_FIELD.DocFee,
    API_FIELD.SoftCost,
    API_FIELD.AssetSoftCost,
    API_FIELD.EquipmentCost,
    API_FIELD.GrossRevenue,
    API_FIELD.TotalAssetCost,
    API_FIELD.TotalAmountFinanced,
    'AmountRequested',
]);
var TITLE_FIELDS = asKeySet([API_FIELD.Title]);
var PHONE_INPUT_MAX_LENGTH = 14;
var PHONE_FIELDS = asKeySet([
    API_FIELD.PhoneNumber,
    API_FIELD.BillingPhoneNumber,
    API_FIELD.MobilePhone,
]);
var EMAIL_FIELDS = asKeySet([API_FIELD.Email, API_FIELD.BillingEmail]);
var PERCENTAGE_FIELDS = asKeySet([API_FIELD.OwnershipPercentage]);
var YEAR_FIELDS = asKeySet([API_FIELD.Year]);
var QUANTITY_FIELDS = asKeySet([API_FIELD.Quantity]);
var emailValidation = function (value) {
    var text = String(value !== null && value !== void 0 ? value : '').trim();
    if (!text) {
        return '';
    }
    var isEveryAddressValid = text
        .split(EMAIL_SEPARATOR_PATTERN)
        .filter(Boolean)
        .every(function (address) { return EMAIL_PATTERN.test(address); });
    return isEveryAddressValid ? '' : 'Enter a valid email address';
};
var findSiblingField = function (context, apiFieldKeys) {
    var match = Object.keys(context.fieldNamesByApiField).find(function (apiFieldName) {
        return apiFieldKeys.has(normaliseConfigKey(apiFieldName));
    });
    return match ? context.fieldNamesByApiField[match] : '';
};
var isCustomerInformationGroup = function (groupKey) {
    var normalisedGroupKey = normaliseConfigKey(groupKey);
    var mappedStepId = STEP_ID_BY_CONFIG_GROUP[normalisedGroupKey];
    return mappedStepId
        ? mappedStepId === CREATE_APPLICATION_STEP_ID.CustomerInformation
        : normalisedGroupKey ===
            normaliseConfigKey(CREATE_APPLICATION_STEP_ID.CustomerInformation);
};
var findSiblingCountryField = function (context) {
    return findSiblingField(context, COUNTRY_FIELDS);
};
var findSiblingStateField = function (context) {
    return findSiblingField(context, STATE_FIELDS);
};
export var enhanceConfiguredField = function (field, context) {
    var fieldKey = normaliseConfigKey(context.apiFieldName);
    var hasConfiguredOptions = Boolean(field.options && field.options.length > 0);
    if (ADDRESS_FIELDS.has(fieldKey)) {
        return __assign(__assign({}, field), { type: FormFieldType.AddressAutoComplete, addressAutofill: {
                address: field.name,
                city: findSiblingField(context, CITY_FIELDS),
                state: findSiblingStateField(context),
                country: findSiblingCountryField(context),
                postalCode: findSiblingField(context, POSTAL_CODE_FIELDS),
            } });
    }
    if (TAXPAYER_ID_FIELDS.has(fieldKey) && isCustomerInformationGroup(context.groupKey)) {
        return __assign(__assign({}, field), { lookupKey: CUSTOMER_LOOKUP_KEY.ExistingCustomerByTaxId });
    }
    if (COUNTRY_FIELDS.has(fieldKey) && !hasConfiguredOptions) {
        var stateFieldName = findSiblingStateField(context);
        return __assign(__assign(__assign({}, field), { options: COUNTRY_OPTIONS }), (stateFieldName && { resetFields: [stateFieldName] }));
    }
    if (STATE_FIELDS.has(fieldKey) && !hasConfiguredOptions) {
        var countryFieldName_1 = findSiblingCountryField(context);
        if (!countryFieldName_1) {
            return field;
        }
        return __assign(__assign({}, field), { getOptions: function (values) { return getStateOptions(values[countryFieldName_1]); }, dependsOn: [countryFieldName_1] });
    }
    if (TITLE_FIELDS.has(fieldKey) && !hasConfiguredOptions) {
        return __assign(__assign({}, field), { options: getAllTitleOptions() });
    }
    if (AMOUNT_FIELDS.has(fieldKey)) {
        return __assign(__assign({}, field), { format: formatAmountInput, validate: amountValidation });
    }
    if (PHONE_FIELDS.has(fieldKey)) {
        return __assign(__assign({}, field), { format: formatPhoneInput, maxLength: PHONE_INPUT_MAX_LENGTH });
    }
    if (EMAIL_FIELDS.has(fieldKey)) {
        return __assign(__assign({}, field), { validate: emailValidation });
    }
    if (PERCENTAGE_FIELDS.has(fieldKey)) {
        return __assign(__assign({}, field), { format: formatPercentageInput, validate: percentageValidation });
    }
    if (YEAR_FIELDS.has(fieldKey)) {
        return __assign(__assign({}, field), { validate: yearValidation });
    }
    if (QUANTITY_FIELDS.has(fieldKey)) {
        return __assign(__assign({}, field), { validate: quantityValidation });
    }
    return field;
};
